/**
 * ExecutionAgent - Suggestions Section
 * 
 * Best practices and suggestions for optimal performance.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { EXECUTION_AGENT_PROMPT_KEYS } from './variables';

class ExecutionAgentSuggestionsSection extends PromptSectionBase {
    protected readonly keywordsKey = EXECUTION_AGENT_PROMPT_KEYS.suggestions;
    protected readonly fallbackContent = `## Suggestions for Best Performance

### Building Areas Effectively

1. **Follow the area specifications closely**
   - Region boundaries are given in normalized coordinates (0-1)
   - Content type (text, chart, table, mixed) guides what to create
   - Purpose describes what the area should communicate

2. **Match reference slide styling**
   - Use readSlide to examine reference slides
   - Copy colors, fonts, and spacing patterns
   - Match the density level of reference slides

3. **Use context intelligently**
   - Global context items apply to ALL slides (logos, brand colors)
   - Per-slide context is specific to that slide
   - Image context IDs (logo_xxx, wikimedia_xxx) render as images
   - Text context IDs (as_xxx, excelread_xxx) provide data

4. **Verify your work**
   - Each buildArea returns a screenshot
   - Check if the content matches expectations
   - If wrong, use rollback and try again

### Common Patterns

**For text areas:**
- Use createShape with type "text"
- Set font family, size, color from style patterns
- Apply line spacing and paragraph spacing appropriately
- Use bullet styles that match reference slides

**For chart areas:**
- Use createChart with artifact IDs from Excel context
- Link to workbook ranges provided in excelContext
- Position chart within area bounds
- Add labels, legends, and annotations

**For table areas:**
- Use createTable with appropriate structure
- Populate with data from context
- Apply cell styling (fonts, colors, borders)
- Match density of reference tables

**For mixed areas:**
- Combine multiple shape types
- Layer elements strategically
- Maintain clear hierarchy
- Respect area boundaries

### Error Handling

- If buildArea fails, read the error message
- Common issues: shape overlap, out-of-bounds, missing context
- Use rollback to undo failed attempts
- Don't get stuck - if retry fails, note it and move on`.trim();
}

const suggestionsSection = new ExecutionAgentSuggestionsSection();

/**
 * Get the suggestions section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getSuggestionsSection(): string {
    return suggestionsSection.getContent();
}

/**
 * Export constant for backwards compatibility
 */
export const EXECUTION_AGENT_SUGGESTIONS = suggestionsSection.getFallbackContent();
